import { notFound, redirect } from "@tanstack/react-router";

import { buildHref, matchRoute } from "@softmax/web-addresses";

import { requireGameResource } from "@/features/game/data";
import { createPlayReader } from "./play-content";

import { onePagerCoworldOf } from "../onepager/coworlds";

type PlayRoute =
  | "game.play"
  | "game.playActivity"
  | "game.playEpisodes"
  | "game.playRequests";

const playRoutes = new Set<PlayRoute>([
  "game.play",
  "game.playActivity",
  "game.playEpisodes",
  "game.playRequests",
]);

export async function playReader({
  signal,
  href,
  expectedRoute,
}: {
  signal?: AbortSignal | undefined;
  href: string;
  expectedRoute?: PlayRoute;
}) {
  if (!URL.canParse(href, window.location.href)) throw notFound();

  const route = matchRoute(new URL(href, window.location.href).pathname);

  if (
    !playRoutes.has(route.name as PlayRoute) ||
    (expectedRoute && route.name !== expectedRoute)
  )
    throw notFound();

  const game = await requireGameResource({ signal, href });

  if (!game.league) throw notFound();

  const address = [game.address.slug, game.league.requestedSlug]
    .filter(Boolean)
    .join("/");
  const reader = createPlayReader({
    signal,
    context: game,
  });

  if (
    !onePagerCoworldOf(game.corpusSlug) &&
    !(await reader.canEnter(game.league.id))
  ) {
    throw redirect({
      href: buildHref({
        name: "game.start",
        game: game.address,
        league: game.league.requestedSlug ?? undefined,
        search: new URL(href, window.location.href).searchParams,
        hash: new URL(href, window.location.href).hash,
      }),
      statusCode: 307,
    });
  }

  return { game, league: game.league, address, reader };
}
